import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Heading, NavLink, Stack, Text, TextLink } from '@eneco/sparky';
import { Fields as SummaryStepFields } from '@sitecore/types/SMESummaryStep';

import { PriceDetailsOverview as PriceDetailsOverviewEneco } from '../../components';
import { FlowContextSme } from '../../context';
import { checkOfferIncludesDynamicPricingProduct, checkOfferIncludesNonDynamicPricingProduct } from '../../helpers';
import { EnergyType, Offer, OfferTermsAndConditions } from '../../types';

type FieldsUnion = Partial<SummaryStepFields>;

interface Props {
  flowContext: FlowContextSme;
  textualData?: FieldsUnion;
  offer?: Offer;
  offerTermsAndConditions?: OfferTermsAndConditions[];
}

export const TermsAndConditions: FC<Props> = ({ textualData, offer, offerTermsAndConditions, flowContext }) => {
  const { energyType } = flowContext;

  const isDynamicPricingContract =
    checkOfferIncludesDynamicPricingProduct(offer) && !checkOfferIncludesNonDynamicPricingProduct(offer);
  const isHybridContract =
    checkOfferIncludesDynamicPricingProduct(offer) && checkOfferIncludesNonDynamicPricingProduct(offer);

  const isRegularContract = !isDynamicPricingContract && !isHybridContract;
  const isRegularContractWithoutEndDate =
    energyType !== EnergyType.Warmth &&
    energyType !== EnergyType.WarmthAndTapWater &&
    energyType !== EnergyType.TapWater &&
    energyType !== EnergyType.GeothermalHeating;

  return (
    <>
      <Stack.Item>
        <Stack direction="column" gap="4">
          <Heading as="h3" size="XS">
            {textualData?.salesConditions?.conditionsParagraph?.value?.title}
          </Heading>

          <Stack.Item>
            <Text>
              <RichText
                html={textualData?.salesConditions?.conditionsParagraph?.value?.content?.split('{conditions}')[0]}
              />{' '}
              <TrackedDialog
                title={textualData?.salesConditions?.salesConditionsDialog?.value?.title}
                description={<RichText html={textualData?.salesConditions?.salesConditionsDialog?.value?.content} />}
                trigger={
                  <TextLink>{textualData?.salesConditions?.salesConditionsDialog?.value?.triggerText}</TextLink>
                }>
                <Stack direction="column" gap="2">
                  {offerTermsAndConditions?.map(({ href, title }) => (
                    <NavLink key={href} href={href} target="_blank">
                      {title}
                    </NavLink>
                  ))}
                </Stack>
              </TrackedDialog>{' '}
              <RichText
                html={
                  textualData?.salesConditions?.conditionsParagraph?.value?.content
                    ?.split('{conditions}')[1]
                    ?.split('{prices}')[0]
                }
              />{' '}
              <TrackedDialog
                title={textualData?.salesConditions?.tariffsDialog?.value?.title}
                description={<RichText html={textualData?.salesConditions?.tariffsDialog?.value?.content} />}
                trigger={<TextLink>{textualData?.salesConditions?.tariffsDialog?.value?.triggerText}</TextLink>}>
                <PriceDetailsOverviewEneco
                  offer={offer}
                  flowContext={flowContext}
                  textualData={textualData as SummaryStepFields}
                />
              </TrackedDialog>{' '}
              <RichText
                html={
                  textualData?.salesConditions?.conditionsParagraph?.value?.content
                    ?.split('{conditions}')[1]
                    ?.split('{prices}')[1]
                }
              />
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>

      {isDynamicPricingContract && (
        <Stack.Item>
          <Stack direction="column" gap="4">
            <Heading as="h3" size="XS">
              {textualData?.salesConditions?.noticePeriodDynamicPricingProductParagraph?.value?.title}
            </Heading>

            <RichText html={textualData?.salesConditions?.noticePeriodDynamicPricingProductParagraph?.value?.content} />
          </Stack>
        </Stack.Item>
      )}

      {isHybridContract && (
        <Stack.Item>
          <Stack direction="column" gap="4">
            <Heading as="h3" size="XS">
              {textualData?.salesConditions?.noticePeriodHybridParagraph?.value?.title}
            </Heading>

            <RichText html={textualData?.salesConditions?.noticePeriodHybridParagraph?.value?.content} />
          </Stack>
        </Stack.Item>
      )}

      {isRegularContract && (
        <>
          <Stack.Item>
            <Stack direction="column" gap="4">
              <Heading as="h3" size="XS">
                {textualData?.salesConditions?.noticePeriodParagraph?.value?.title}
              </Heading>

              <RichText html={textualData?.salesConditions?.noticePeriodParagraph?.value?.content} />
            </Stack>
          </Stack.Item>

          {/* won't show contract extension terms for the energy types without end date */}
          {!isRegularContractWithoutEndDate && (
            <Stack.Item>
              <Stack direction="column" gap="4">
                <Heading as="h3" size="XS">
                  {textualData?.salesConditions?.extensionParagraph?.value?.title}
                </Heading>

                <RichText html={textualData?.salesConditions?.extensionParagraph?.value?.content} />
              </Stack>
            </Stack.Item>
          )}
        </>
      )}

      <Stack.Item>
        <Stack direction="column" gap="4">
          <Heading as="h3" size="XS">
            {textualData?.salesConditions?.objectionParagraph?.value?.title}
          </Heading>

          <RichText html={textualData?.salesConditions?.objectionParagraph?.value?.content} />
        </Stack>
      </Stack.Item>

      {isDynamicPricingContract && (
        <Stack.Item>
          <Stack direction="column" gap="4">
            <Heading as="h3" size="XS">
              {textualData?.salesConditions?.variablePricingParagraph?.value?.title}
            </Heading>

            <RichText html={textualData?.salesConditions?.variablePricingParagraph?.value?.content} />
          </Stack>
        </Stack.Item>
      )}

      {isHybridContract && (
        <Stack.Item>
          <Stack direction="column" gap="4">
            <Heading as="h3" size="XS">
              {textualData?.salesConditions?.variablePricingHybridParagraph?.value?.title}
            </Heading>

            <RichText html={textualData?.salesConditions?.variablePricingHybridParagraph?.value?.content} />
          </Stack>
        </Stack.Item>
      )}
    </>
  );
};
